// @ts-nocheck
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import getLocalizedDateTime from "@insite/client-framework/Common/Utilities/getLocalizedDateTime";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import { OrderModel, OrderSettingsModel } from "@insite/client-framework/Types/ApiModels";
import OrderDetailPageTypeLink from "@insite/content-library/Components/OrderDetailPageTypeLink";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import React, { FC } from "react";
// import { connect } from "react-redux"; Bunzl Custom Code

// Store Custom Code - >
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import { DataTableCellBaseProps } from "@insite/mobius/DataTable/DataTableCellBase";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";

import useCanSeePrices from "@store/Framework/Store/Data/Accounts/Hooks/useCanSeePrices";
import StoreOrderStatus from "@store/Library/Components/Foundation/StoreOrder/StoreOrderStatus";
import { historyTableProps } from "@store/Themes/common/props/history";
import { css } from "styled-components";

/* Store Custom Code - Start */
import siteMessage from "@insite/client-framework/SiteMessage";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import reorder from "@insite/client-framework/Store/Pages/OrderHistory/Handlers/Reorder";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import HistoryContext, { HasHistory, withHistory } from "@insite/mobius/utilities/HistoryContext";
import Button, { ButtonPresentationProps } from "@store/MobiusOverrides/Button/Button";
import { connect, ResolveThunks } from "react-redux";

import { TypographyPresentationProps } from "@store/MobiusOverrides/Typography/Typography";
import ga from "@toolkit/Framework/Common/GoogleAnalytics";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";

const mapStateToProps = (state: ApplicationState) => ({
    language: state.context.session.language,
    showPoNumber: getSettingsCollection(state).orderSettings.showPoNumber,
    isReordering: state.pages.orderHistory.isReordering,
    isUpdatingCart: state.context.isUpdatingCart,
    showAddToCartConfirmationDialog: getSettingsCollection(state).productSettings.showAddToCartConfirmationDialog,
    canReorderItems: getSettingsCollection(state).orderSettings.canReorderItems,
    isVmiOrderHistoryPage: state.pages.orderHistory.isVmiOrderHistoryPage,
});

const mapDispatchToProps = {
    reorder,
};

interface OwnProps {
    order: OrderModel;
    orderSettings: OrderSettingsModel;
    extendedStyles?: OrderSummaryCardStyles;
    // Store Custom Code
    trackAddToCart?: ReturnType<typeof ga.use.trackAddToCart>;
}

type Props = ReturnType<typeof mapStateToProps> &
    OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    HasToasterContext &
    HasHistory;
/* Store Custom Code - End */

export interface OrderSummaryCardStyles {
    orderSummaryCardGridItem?: GridItemProps;
    container?: GridContainerProps;
    pageTypeLinkGridItem?: GridItemProps;
    orderNumberGridItem?: GridItemProps;
    orderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    statusGridItem?: GridItemProps;
    statusHeadingAndText?: SmallHeadingAndTextStyles;
    webOrderNumberGridItem?: GridItemProps;
    webOrderNumberHeadingAndText?: SmallHeadingAndTextStyles;
    totalGridItem?: GridItemProps;
    totalHeadingAndText?: SmallHeadingAndTextStyles;
    // Starter Custom Code
    orderNumberCell?: DataTableCellProps;
    dateCell?: DataTableCellProps;
    shipToCell?: DataTableCellProps;
    customerPOCell?: DataTableCellProps;
    statusCell?: DataTableCellProps;
    totalCell?: DataTableCellProps;
    // Foundation
    cellOrderStatus?: DataTableCellBaseProps;
    // Bunzl
    reorderCells?: DataTableCellProps;
    reorderButton?: ButtonPresentationProps;
}

export const orderSummaryCardStyles: OrderSummaryCardStyles = {
    orderNumberCell: historyTableProps.orderNumberCell,
    dateCell: historyTableProps.dateCell,
    customerPOCell: historyTableProps.customerPOCell,
    statusCell: historyTableProps.statusCell,
    totalCell: historyTableProps.totalCell,
    /* Store Custom Code - Start */
    reorderCells: {
        alignX: "right",
    },
    shipToCell: historyTableProps.shipToCell,
    reorderButton: {
        color: "primary",
        sizeVariant: "small",
        size: 32,
    },
    /* Store Custom Code - End */
};

const OrderSummaryCard: FC<Props> = ({
    /* Store Custom Code - Start */
    language,
    order,
    orderSettings,
    showPoNumber,
    extendedStyles,
    reorder,
    toaster,
    history,
    isReordering,
    isUpdatingCart,
    showAddToCartConfirmationDialog,
    canReorderItems,
    isVmiOrderHistoryPage,
    trackAddToCart,
    /* Store Custom Code - End */
}) => {
    const [styles] = React.useState(() => mergeToNew(orderSummaryCardStyles, extendedStyles));

    /* Store Custom Code - Start */
    const canSeePrices = useCanSeePrices();
    /* Store Custom Code - End */

    const orderDateDisplay = getLocalizedDateTime({
        dateTime: order.orderDate,
        language,
        options: {
            year: "numeric",
            month: "numeric",
            day: "numeric",
        },
    });

    /* Starter Custom Code */
    const orderNumber = orderSettings.showWebOrderNumber
        ? order.webOrderNumber || order.erpOrderNumber
        : order.erpOrderNumber || order.webOrderNumber;

    /* Store Custom Code - Start */
    const linkOrderNumber = order.erpOrderNumber || order.webOrderNumber;

    const shipTo = `${order.stAddress1} ${order.stAddress2} ${order.shipToCity} ${order.shipToState}`;
    const shipToTitle = `${order.stAddress1} ${order.stAddress2}${order.stAddress3 ? ` ${order.stAddress3} ` : " "}${
        order.shipToCity
    } ${order.shipToState}`;
    const isVmiOrder = !!order.vmiLocationId;

    const reorderClick = () => {
        return reorder({
            orderNumber,
            onError: message => onReorderError(message),
            onComplete: resultProps => {
                if (resultProps.apiResult?.cartLines) {
                    const didAddAllToCart = resultProps.apiResult.cartLines.notAllAddedToCart === false;
                    onReorderSuccess(orderNumber, linkOrderNumber, isVmiOrder, didAddAllToCart);
                }
            },
        });
    };

    const onReorderSuccess = (
        orderNumber: string,
        linkOrderNumber: string,
        isVmiOrder: boolean,
        canAddAllToCart: boolean,
    ) => {
        if (!showAddToCartConfirmationDialog) {
            return;
        }
        toaster.addToast({
            body: (
                <HistoryContext.Provider value={{ history }}>
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                        isVmiOrderDetailsPage={isVmiOrderHistoryPage}
                    />
                    &nbsp;
                    {!canAddAllToCart ? siteMessage("notAllAddedToCart") : translate("Added to Cart")}
                </HistoryContext.Provider>
            ),
            messageType: "success",
            timeoutLength: 6000,
        });

        if (trackAddToCart) {
            trackAddToCart({ items: order.orderLines?.filter(x => x.isActiveProduct) });
        }
    };
    const onReorderError = (message: string) => {
        toaster.addToast({
            body: message,
            messageType: "danger",
            timeoutLength: 6000,
        });
    };
    /* Store Custom Code - End */

    return (
        // Store Custom Code - custom compact recent orders --->
        <DataTableRow key={order.id}>
            <DataTableCell {...styles.orderNumberCell}>
                {orderNumber ? (
                    <OrderDetailPageTypeLink
                        title={orderNumber}
                        orderNumber={linkOrderNumber}
                        isVmiOrder={isVmiOrder}
                    />
                ) : (
                    "--"
                )}
            </DataTableCell>
            <DataTableCell {...styles.dateCell}>{orderDateDisplay}</DataTableCell>
            {canSeePrices && <DataTableCell {...styles.totalCell}>{order.orderGrandTotalDisplay}</DataTableCell>}
            <DataTableCell {...styles.statusCell}>
                <StoreOrderStatus status={order.statusDisplay} />
            </DataTableCell>
            <DataTableCell
                {...styles.shipToCell}
                /* Store Custom Code - typography props is typed as typography presentation props so we need to cast it to
                use the title prop */
                typographyProps={{ title: shipToTitle } as TypographyPresentationProps}
            >
                {shipTo}
            </DataTableCell>
            {showPoNumber && (
                <DataTableCell {...styles.customerPOCell}>{order.customerPO ? order.customerPO : "--"}</DataTableCell>
            )}
            {canReorderItems && !isVmiOrderHistoryPage && (
                <DataTableCell {...styles.reorderCells}>
                    <Button
                        disabled={Object.keys(isReordering).length > 0 || isUpdatingCart}
                        {...styles.reorderButton}
                        onClick={reorderClick}
                        data-test-selector="orderSummaryCard_Reorder"
                        isLoading={isReordering[orderNumber]}
                    >
                        {translate("Reorder")}
                    </Button>
                </DataTableCell>
            )}
        </DataTableRow>
        // <--- Store Custom Code
    );
};

// Store Custom Code
export default connect(mapStateToProps, mapDispatchToProps)(withToaster(withHistory(OrderSummaryCard)));
